import React from 'react';
import { useSelector } from 'react-redux';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';

import { AuthRoutes } from '@klpkonsern/auth-core/routes';
import { BuildingTabs } from '@klp/bygg/classes/BuildingTabs';
import { ByggRoutes } from '@klp/bygg/config';
import { ByggSingle } from '@klp/bygg/views/ByggSingle';
import { RequireClientChoice } from '@klp/client/components/RequireClientChoice';
import { ClientSelector } from '@klp/client/components/Select';
import { ClientRoutes } from '@klp/client/config';
import { ByggClientListPreselect } from '@klp/client/views/ByggClientListPreselect';
import { ByggClientPreselect } from '@klp/client/views/ByggClientPreselect';
import {
    ContractManagementRoutes,
    ContractManagementView,
} from '@klp/contractmanagement';
import { useAppParams } from '@klp/core/app/useAppParams';
import { FakturaListView } from '@klp/faktura';
import { FakturaRoutes } from '@klp/faktura/config';
import {
    LeaseAgreementTable,
    LeaseAgreementRoutes,
} from '@klp/leaseagreements';
import { LevendeKontrakter, KontraktRoutes } from '@klp/levendekontrakter';
import {
    MessageAdd,
    MessageEdit,
    MessageList,
    MessageSingle,
} from '@klp/messageboard';
import { MessageBoardRoutes } from '@klp/messageboard/config';
import { ProjectDxboFrame } from '@klp/projectdxbo';
import { ProjectDxboRoutes } from '@klp/projectdxbo/config';
import { SakAdd, SakListView, SakView } from '@klp/sak';
import { SakRoutes } from '@klp/sak/config';
import { SupportFormRoutes } from '@klp/supportapplicationform/config';
import {
    ApplicationFormAdd,
    ApplicationFormView,
} from '@klp/supportapplicationform/views';
import { MiljoprofilRoutes } from '@klp/sustainability/config';
import { Miljoprofil } from '@klp/sustainability/views/Miljoprofil';
import { SearchUsers } from '@klp/user';
import { UserRoutes } from '@klp/user/config';
import { ListUsers } from '@klp/user/Views/ListUsers';
import { RegisterUser } from '@klp/user/Views/RegisterUser';
import { ParamType } from '@klp/utils';

import {
    LogoutView,
    AuthCallbackView,
    SilentCallbackView,
    useCurrentUser,
} from '../auth';
import { Frontpage } from '../frontpage';
import { FrontpageRoutes } from '../frontpage/config';
import { ByggList } from '../packages/bygg/views/ByggList';
import { appSelectors } from '../packages/core/app';

export const AuthorisedRoutes: React.FC = () => {
    const { isAuthenticated, currentUserIsLoading, isAdmin } = useCurrentUser();
    const { selectedClient } = useAppParams();
    const { pathname } = useLocation();

    // Listen to app trigger to force re-render when client state changes
    useSelector(appSelectors);

    // Guardian for authenticated access only (user has valid token and is logged in)
    if (!isAuthenticated) return null;
    if (currentUserIsLoading) return null; // Wait for user data to load, authorization is not determined yet

    return (
        <Switch>
            <Route
                path={AuthRoutes.logout}
                exact={true}
                component={LogoutView}
            />
            <Route
                path={AuthRoutes.authCallback}
                exact={true}
                component={AuthCallbackView}
            />
            <Route
                path={AuthRoutes.authSilentCallback}
                exact={true}
                component={SilentCallbackView}
            />

            <Route
                path={ClientRoutes.clientSelect()}
                exact={true}
                component={ClientSelector}
            />

            <Route
                path={ClientRoutes.byggView(':kundeNummer', ':byggNummer')}
                exact={true}
                component={ByggClientPreselect}
            />

            {!isAdmin && !selectedClient && (
                <Redirect to={ClientRoutes.clientSelect(pathname ?? '/')} />
            )}
            <Route
                path={SakRoutes.list()}
                exact={true}
                component={SakListView}
            />
            <Route path={SakRoutes.add()} exact={true} component={SakAdd} />
            <Route
                path={SakRoutes.single(':sakId')}
                exact={true}
                component={SakView}
            />
            <Redirect
                from={SakRoutes.singleLegacy(':sakId')}
                to={SakRoutes.single(':sakId')}
            />

            <Route
                path={ClientRoutes.byggListView(':kundeNummer')}
                exact={true}
                component={ByggClientListPreselect}
            />
            <Route
                path={SupportFormRoutes.form()}
                exact={true}
                component={ApplicationFormAdd}
            />
            <Route
                path={SupportFormRoutes.formAdd(':formId')}
                exact={true}
                component={ApplicationFormAdd}
            />
            <Route
                path={SupportFormRoutes.formView(':formId')}
                exact={true}
                component={ApplicationFormView}
            />
            <Route
                path={SupportFormRoutes.formView()}
                exact={true}
                component={ApplicationFormView}
            />

            <Route
                path={UserRoutes.search()}
                exact={true}
                component={SearchUsers}
            />
            {/* Must stay non-exact: RequireClientChoice must stay mounted across list <-> register navigation */}
            <Route path={UserRoutes.moduleRoute()}>
                <RequireClientChoice>
                    <Switch>
                        <Route
                            path={UserRoutes.list()}
                            exact={true}
                            component={ListUsers}
                        />
                        <Route
                            path={UserRoutes.register()}
                            exact={true}
                            component={RegisterUser}
                        />
                        <Redirect to={UserRoutes.list()} />
                    </Switch>
                </RequireClientChoice>
            </Route>

            <Route path={ByggRoutes.list()} exact={true} component={ByggList} />
            <Route
                path={ByggRoutes.listAll()}
                exact={true}
                render={() => <ByggList showAll={true} />}
            />
            <Route
                path={ByggRoutes.listAll(':byggNummer')}
                exact={true}
                render={(props) => (
                    <ByggList
                        showAll={true}
                        selectedBygg={props.match.params.byggNummer}
                    />
                )}
            />
            <Route
                path={ByggRoutes.single(':byggNummer', ':tab')}
                exact={true}
                component={ByggSingle}
            />
            <Route
                path={ByggRoutes.singleKundeportal(':byggNummer', ':tab')}
                exact={true}
                render={(props) => (
                    <Redirect
                        to={ByggRoutes.single(
                            props.match.params.byggNummer,
                            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                            BuildingTabs[
                                props.match.params
                                    .tab! as keyof typeof BuildingTabs
                            ],
                        ).concat(props.location.hash || '')}
                    />
                )}
            />

            <Route
                path={ByggRoutes.single(':byggNummer')}
                exact={true}
                component={ByggSingle}
            />
            <Route
                path={FakturaRoutes.list()}
                exact={true}
                component={FakturaListView}
            />
            <Route
                path={MessageBoardRoutes.list()}
                exact={true}
                component={MessageList}
            />
            <Route
                path={MessageBoardRoutes.add()}
                exact={true}
                component={MessageAdd}
            />
            <Route
                path={MessageBoardRoutes.single(':messageId')}
                exact={true}
                component={MessageSingle}
            />
            <Route
                path={MessageBoardRoutes.edit(':messageId')}
                exact={true}
                component={MessageEdit}
            />

            <Route
                path={FrontpageRoutes.frontpage()}
                exact={true}
                component={Frontpage}
            />

            <Route
                path={ContractManagementRoutes.moduleRoute()}
                exact={true}
                component={ContractManagementView}
            />
            <Route
                path={ContractManagementRoutes.tab(':tab')}
                component={ContractManagementView}
            />

            <Route
                path={KontraktRoutes.kontrakt()}
                exact={true}
                component={LevendeKontrakter}
            />
            <Route
                path={KontraktRoutes.kontraktDetaljer(
                    `:${ParamType.contractPath}+`,
                )}
                component={LevendeKontrakter}
            />

            <Route
                path={ProjectDxboRoutes.projectDxbo()}
                exact={true}
                component={ProjectDxboFrame}
            />
            <Route
                path={ProjectDxboRoutes.projectDxboDetails(
                    `:${ParamType.dxboPath}+`,
                )}
                component={ProjectDxboFrame}
            />
            <Route
                path={LeaseAgreementRoutes.moduleRoute()}
                exact={true}
                component={LeaseAgreementTable}
            />
            <Route
                path={MiljoprofilRoutes.moduleRoute()}
                exact={true}
                component={Miljoprofil}
            />

            <Route
                path="/kundeportal/:a(.*)"
                render={({ match }) => <Redirect to={'/' + match.params.a} />}
            />

            <Redirect to={FrontpageRoutes.frontpage()} />
        </Switch>
    );
};
